// Member gate shown when a non-subscriber tries a members-only action (e.g.
// starting a Kiki). It pitches membership, then on "Subscribe" loads the
// subscription plans and hands off to the shared PaymentModal in
// subscription-only mode. Self-contained so any callsite can drop it in.
import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Sparkles, Loader2, Check } from "lucide-react";
import { UserAPI } from "@shared/types";
import { useGetPaymentOptions } from "../lib/api/usePayment";
import { useInvalidateProfile } from "../lib/api/useProfile";
import { PaymentModal } from "./booking/PaymentModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Headline + supporting copy for the pitch step. */
  title?: string;
  description?: string;
  /** Fired after a successful subscription, before the gate closes. */
  onSubscribed?: () => void;
}

const PERKS = [
  "Start your own Kikis and rally a crew",
  "A seat at Outclose dinners, matched with compatible tablemates",
  "See who's looking to meet up — unlimited outings and connections",
];

export function SubscribeInterstitial({
  isOpen,
  onClose,
  title = "Kikis are a member perk",
  description = "Join Outclose to start your own Kiki and meet people before the event.",
  onSubscribed,
}: Props) {
  const getPaymentOptions = useGetPaymentOptions();
  const invalidateProfile = useInvalidateProfile();
  const [paymentOptions, setPaymentOptions] = useState<UserAPI.PaymentOption[] | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);

  const handleSubscribe = async () => {
    const response = await getPaymentOptions.mutateAsync({});
    if (response.success && response.data) {
      setPaymentOptions(response.data.paymentOptions);
      setStartDate(response.data.startDate || null);
    }
  };

  const handleClose = () => {
    setPaymentOptions(null);
    onClose();
  };

  const handleSuccess = () => {
    invalidateProfile();
    setPaymentOptions(null);
    onSubscribed?.();
    onClose();
  };

  if (!isOpen) return null;

  // Once plans are loaded, hand the full sell-sheet over to the payment modal.
  if (paymentOptions) {
    return (
      <PaymentModal
        subscriptionOnly
        paymentOptions={paymentOptions}
        startDate={startDate}
        onSuccess={handleSuccess}
        onCancel={handleClose}
      />
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-neutral-900 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-r from-primary-400 via-primary-500 to-violet-500" />

        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
        >
          <X className="w-4 h-4 text-neutral-500" />
        </button>

        <div className="p-6 pb-8 sm:pb-6">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500 to-violet-500 flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>

          <h2 className="text-lg font-bold text-neutral-900 dark:text-white">{title}</h2>
          <p className="mt-1.5 text-sm text-neutral-500 dark:text-neutral-400">{description}</p>

          <ul className="mt-4 space-y-2.5">
            {PERKS.map((perk) => (
              <li key={perk} className="flex items-start gap-2.5">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-primary-500/15 text-primary-600 dark:text-primary-400 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3" />
                </span>
                <span className="text-sm text-neutral-700 dark:text-neutral-200">{perk}</span>
              </li>
            ))}
          </ul>

          <button
            type="button"
            onClick={handleSubscribe}
            disabled={getPaymentOptions.isPending}
            className="mt-6 w-full py-3 rounded-full bg-black dark:bg-primary-600 text-white text-sm font-semibold hover:bg-neutral-800 dark:hover:bg-primary-500 disabled:opacity-50 transition-colors"
          >
            {getPaymentOptions.isPending ? (
              <span className="flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Loading plans…
              </span>
            ) : (
              "See membership plans"
            )}
          </button>
          <button
            type="button"
            onClick={handleClose}
            className="mt-2 w-full py-2 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 transition-colors"
          >
            Maybe later
          </button>

          <p className="mt-3 text-center text-xs text-neutral-400 dark:text-neutral-500">
            No subscription? You can still join other people's Kikis for free.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
